import React, { useEffect, useState } from 'react';
import axios from 'axios';

import type { ClaimNotificationModel, UpdateNotification } from '~/components/ClaimNotificationsCard/types';
import HoverChangeField from '~/components/HoverChangeField';
import {
  areSameLocalDate,
  compareDatesStrings,
  DATE_COMPARISON,
  isDateBeforeToday,
  localToServerDateTimeNoonUTC,
  serverDateTimeToLocalDate,
  serverDateToLocal,
} from '~/DateTimeUtils';
import { reportAxiosError } from '~/Utils';

interface DueDateProps {
  notification: ClaimNotificationModel;
  disabled?: boolean;
  setIsUpdating: (isUpdating: boolean) => void;
  onUpdate: UpdateNotification;
  markOverdue?: boolean;
}

const NotificationDueDate: React.FC<DueDateProps> = ({
  notification,
  disabled,
  setIsUpdating,
  onUpdate,
  markOverdue,
}) => {
  const [dueDateTimeToDisplay, setDueDateTimeToDisplay] = useState<string | null | undefined>();

  const notificationDueDateTime = notification?.due_datetime;
  useEffect(() => {
    setDueDateTimeToDisplay(notificationDueDateTime);
  }, [notificationDueDateTime]);

  const isOverdueDate = dueDateTimeToDisplay ? isDateBeforeToday(dueDateTimeToDisplay) : false;

  const handleDueDateUpdate = async ({ due_date: newLocalDate }: { due_date: string }) => {
    const originalDueDateTime = notification.due_datetime;
    const isSameDay = areSameLocalDate(newLocalDate, notification.datetime);
    // Format to 12:00:00 UTC before sending to backend
    const proposedDateStr = isSameDay
      ? localToServerDateTimeNoonUTC(notification.datetime)
      : localToServerDateTimeNoonUTC(newLocalDate);

    if (compareDatesStrings(proposedDateStr, notification.datetime) === DATE_COMPARISON.BEFORE) {
      // Revert optimistic update and prevent API call for invalid date
      setDueDateTimeToDisplay(originalDueDateTime);
      return;
    }

    try {
      setIsUpdating(true);
      setDueDateTimeToDisplay(proposedDateStr);

      await axios.patch(`/api/v1/notifications/${notification.id}/due_date`, {
        claim_id: notification.claim_id,
        due_datetime: proposedDateStr,
      });

      onUpdate(notification.id, { due_datetime: proposedDateStr });
    } catch (err) {
      await reportAxiosError(err);
      setDueDateTimeToDisplay(originalDueDateTime);
    } finally {
      setIsUpdating(false);
    }
  };

  return dueDateTimeToDisplay ? (
    <HoverChangeField
      name="due_date"
      specialFieldType="date"
      specialFieldAdditionalProps={{
        minDate: serverDateTimeToLocalDate(notification.datetime),
      }}
      value={serverDateTimeToLocalDate(dueDateTimeToDisplay)}
      onUpdate={handleDueDateUpdate}
      label="Due Date"
      permanent
      disabled={disabled}
    >
      <div className={markOverdue && isOverdueDate ? 'text-red-700' : undefined}>
        {serverDateToLocal(dueDateTimeToDisplay)}
      </div>
    </HoverChangeField>
  ) : null;
};

export default NotificationDueDate;
